import { Button, Form, Input, Modal } from 'antd';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNotify } from '@/hooks/useNotify';
import { useGlobalLoader } from '@/hooks/useGlobalLoader';
import { useMutationReset } from '@/hooks/useMutationReset';
import {
  useCreateCollectionMutation,
  useUpdateCollectionMutation,
} from '@/modules/collections/collectionsApi';
import type { Collection } from '@/types/collection';
import { resolveMutationResult } from '@/utils/apiResponse';
import styles from './CollectionFormModal.module.scss';

interface CollectionFormModalProps {
  open: boolean;
  collection?: Collection | null;
  onClose: () => void;
}

interface CollectionFormValues {
  name: string;
}

export function CollectionFormModal({ open, collection, onClose }: CollectionFormModalProps) {
  const notify = useNotify();
  const isEdit = Boolean(collection);
  const [createCollection, createState] = useCreateCollectionMutation();
  const [updateCollection, updateState] = useUpdateCollectionMutation();
  const isLoading = createState.isLoading || updateState.isLoading;
  useGlobalLoader(isLoading);

  const { control, handleSubmit, reset } = useForm<CollectionFormValues>({
    defaultValues: {
      name: '',
    },
  });

  useMutationReset(createState.reset);
  useMutationReset(updateState.reset);

  useEffect(() => {
    if (!open) return;

    reset({
      name: collection?.name ?? '',
    });
  }, [collection, open, reset]);

  const onSubmit = async (values: CollectionFormValues) => {
    const payload = {
      name: values.name.trim(),
    };

    if (isEdit) {
      if (!collection?.id) return;

      const result = await resolveMutationResult(updateCollection({ id: collection.id, body: payload }));
      if (result.ok) {
        notify.success(result.message ?? 'Collection updated successfully.');
        onClose();
        return;
      }

      notify.error(result.message);
      return;
    }

    const result = await resolveMutationResult(createCollection(payload));

    if (result.ok) {
      notify.success(result.message ?? 'Collection created successfully.');
      onClose();
      return;
    }

    notify.error(result.message);
  };

  return (
    <Modal
      title={isEdit ? 'Edit collection' : 'Add collection'}
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnHidden
      width={400}
    >
      <Form layout="vertical" className={styles.form} onFinish={handleSubmit(onSubmit)}>
        <Controller
          name="name"
          control={control}
          rules={{ required: 'Name is required' }}
          render={({ field, fieldState }) => (
            <Form.Item
              label="Name"
              required
              validateStatus={fieldState.error ? 'error' : ''}
              help={fieldState.error?.message}
            >
              <Input {...field} placeholder="e.g. New Arrival, Best Seller" />
            </Form.Item>
          )}
        />

        <div className={styles.actions}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" htmlType="submit" loading={isLoading}>
            {isEdit ? 'Save changes' : 'Create collection'}
          </Button>
        </div>
      </Form>
    </Modal>
  );
}
